import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, AlertCircle } from "lucide-react";
import { useAccounts } from "@/features/accounts/hooks/useAccounts";
import type { AccountingConfig } from "../types";
import type { Account } from "@/features/accounts/types";

interface AccountingConfigFormProps {
  config: AccountingConfig | null | undefined;
  onSave: (config: AccountingConfig) => Promise<void>;
  isLoading?: boolean;
}

/**
 * Form for configuring automatic journal entry creation from tax documents.
 */
export function AccountingConfigForm({
  config,
  onSave,
  isLoading = false,
}: AccountingConfigFormProps) {
  const { accounts, isLoading: isLoadingAccounts } = useAccounts();

  // Form state
  const [enabled, setEnabled] = useState(config?.enabled ?? false);
  const [autoPost, setAutoPost] = useState(config?.autoPost ?? false);
  const [inputVatAccountId, setInputVatAccountId] = useState(config?.inputVatAccountId ?? "");
  const [outputVatAccountId, setOutputVatAccountId] = useState(config?.outputVatAccountId ?? "");
  const [accountsPayableAccountId, setAccountsPayableAccountId] = useState(config?.accountsPayableAccountId ?? "");
  const [accountsReceivableAccountId, setAccountsReceivableAccountId] = useState(config?.accountsReceivableAccountId ?? "");
  const [defaultExpenseAccountId, setDefaultExpenseAccountId] = useState(config?.defaultExpenseAccountId ?? "");
  const [defaultRevenueAccountId, setDefaultRevenueAccountId] = useState(config?.defaultRevenueAccountId ?? "");

  // Update form state when config changes
  useEffect(() => {
    setEnabled(config?.enabled ?? false);
    setAutoPost(config?.autoPost ?? false);
    setInputVatAccountId(config?.inputVatAccountId ?? "");
    setOutputVatAccountId(config?.outputVatAccountId ?? "");
    setAccountsPayableAccountId(config?.accountsPayableAccountId ?? "");
    setAccountsReceivableAccountId(config?.accountsReceivableAccountId ?? "");
    setDefaultExpenseAccountId(config?.defaultExpenseAccountId ?? "");
    setDefaultRevenueAccountId(config?.defaultRevenueAccountId ?? "");
  }, [config]);

  // Filter accounts by type (only non-header accounts)
  const postableAccounts = useMemo(() => {
    return accounts.filter((a) => !a.isHeader && a.isActive);
  }, [accounts]);

  // IVA accounts (typically ASSET type for IVA Crédito Fiscal)
  const vatAccounts = useMemo(() => {
    return postableAccounts.filter(
      (a) => a.accountType === "ASSET" && a.accountSubtype === "CURRENT_ASSET"
    );
  }, [postableAccounts]);

  // Liability accounts for Accounts Payable
  const liabilityAccounts = useMemo(() => {
    return postableAccounts.filter(
      (a) => a.accountType === "LIABILITY" && a.accountSubtype === "CURRENT_LIABILITY"
    );
  }, [postableAccounts]);

  // Expense accounts
  const expenseAccounts = useMemo(() => {
    return postableAccounts.filter((a) => a.accountType === "EXPENSE");
  }, [postableAccounts]);

  // Revenue accounts
  const revenueAccounts = useMemo(() => {
    return postableAccounts.filter((a) => a.accountType === "REVENUE");
  }, [postableAccounts]);

  // Asset accounts for Accounts Receivable
  const assetAccounts = useMemo(() => {
    return postableAccounts.filter(
      (a) => a.accountType === "ASSET" && a.accountSubtype === "CURRENT_ASSET"
    );
  }, [postableAccounts]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    await onSave({
      enabled,
      autoPost,
      inputVatAccountId: inputVatAccountId || undefined,
      outputVatAccountId: outputVatAccountId || undefined,
      accountsPayableAccountId: accountsPayableAccountId || undefined,
      accountsReceivableAccountId: accountsReceivableAccountId || undefined,
      defaultExpenseAccountId: defaultExpenseAccountId || undefined,
      defaultRevenueAccountId: defaultRevenueAccountId || undefined,
    });
  };

  // Validation
  const isValid =
    !enabled ||
    (inputVatAccountId && accountsPayableAccountId && defaultExpenseAccountId);

  const hasAccounts = accounts.length > 0;

  if (isLoadingAccounts) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!hasAccounts) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <AlertCircle className="h-8 w-8 text-muted-foreground mb-2" />
        <p className="text-muted-foreground">
          No hay cuentas configuradas. Primero debe crear un plan de cuentas.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Enable toggle */}
      <div className="flex items-center space-x-3">
        <Checkbox
          id="enabled"
          checked={enabled}
          onCheckedChange={(checked) => setEnabled(checked === true)}
        />
        <div className="space-y-0.5">
          <Label htmlFor="enabled" className="text-base font-medium cursor-pointer">
            Habilitar centralización contable
          </Label>
          <p className="text-sm text-muted-foreground">
            Permite crear asientos contables desde el libro de compras y ventas
          </p>
        </div>
      </div>

      {/* Account mappings - only shown when enabled */}
      {enabled && (
        <div className="space-y-4 pl-7 border-l-2 border-muted ml-2">
          <p className="text-sm font-medium">Compras</p>

          {/* IVA Crédito Fiscal */}
          <div className="space-y-2">
            <Label htmlFor="inputVatAccount">IVA Crédito Fiscal (19%)</Label>
            <AccountSelect
              value={inputVatAccountId}
              onValueChange={setInputVatAccountId}
              accounts={vatAccounts}
              placeholder="Seleccionar cuenta de IVA CF..."
            />
            <p className="text-xs text-muted-foreground">
              Cuenta donde se registra el IVA de compras
            </p>
          </div>

          {/* Cuentas por Pagar */}
          <div className="space-y-2">
            <Label htmlFor="accountsPayable">Cuentas por Pagar</Label>
            <AccountSelect
              value={accountsPayableAccountId}
              onValueChange={setAccountsPayableAccountId}
              accounts={liabilityAccounts}
              placeholder="Seleccionar cuenta de proveedores..."
            />
            <p className="text-xs text-muted-foreground">
              Cuenta de pasivo para facturas por pagar
            </p>
          </div>

          {/* Gasto por Defecto */}
          <div className="space-y-2">
            <Label htmlFor="defaultExpense">Gasto por Defecto</Label>
            <AccountSelect
              value={defaultExpenseAccountId}
              onValueChange={setDefaultExpenseAccountId}
              accounts={expenseAccounts}
              placeholder="Seleccionar cuenta de gastos..."
            />
            <p className="text-xs text-muted-foreground">
              Cuenta de gasto predeterminada para facturas recibidas
            </p>
          </div>

          {/* Divider */}
          <div className="border-t pt-4 mt-4">
            <p className="text-sm font-medium mb-3">Ventas</p>
          </div>

          {/* IVA Débito Fiscal */}
          <div className="space-y-2">
            <Label htmlFor="outputVatAccount">IVA Débito Fiscal (19%)</Label>
            <AccountSelect
              value={outputVatAccountId}
              onValueChange={setOutputVatAccountId}
              accounts={liabilityAccounts}
              placeholder="Seleccionar cuenta de IVA DF..."
            />
            <p className="text-xs text-muted-foreground">
              Cuenta donde se registra el IVA de ventas
            </p>
          </div>

          {/* Cuentas por Cobrar */}
          <div className="space-y-2">
            <Label htmlFor="accountsReceivable">Cuentas por Cobrar</Label>
            <AccountSelect
              value={accountsReceivableAccountId}
              onValueChange={setAccountsReceivableAccountId}
              accounts={assetAccounts}
              placeholder="Seleccionar cuenta de clientes..."
            />
            <p className="text-xs text-muted-foreground">
              Cuenta de activo para facturas por cobrar
            </p>
          </div>

          {/* Ingreso por Defecto */}
          <div className="space-y-2">
            <Label htmlFor="defaultRevenue">Ingreso por Defecto</Label>
            <AccountSelect
              value={defaultRevenueAccountId}
              onValueChange={setDefaultRevenueAccountId}
              accounts={revenueAccounts}
              placeholder="Seleccionar cuenta de ingresos..."
            />
            <p className="text-xs text-muted-foreground">
              Cuenta de ingreso predeterminada para facturas emitidas
            </p>
          </div>

          {/* Auto-post toggle */}
          <div className="flex items-center space-x-3 pt-2">
            <Checkbox
              id="autoPost"
              checked={autoPost}
              onCheckedChange={(checked) => setAutoPost(checked === true)}
            />
            <div className="space-y-0.5">
              <Label htmlFor="autoPost" className="cursor-pointer">
                Contabilizar automáticamente
              </Label>
              <p className="text-xs text-muted-foreground">
                {autoPost
                  ? "Los asientos se contabilizarán automáticamente"
                  : "Los asientos se crearán como borrador para revisión"}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Submit button */}
      <div className="flex justify-end pt-4">
        <Button type="submit" disabled={isLoading || !isValid}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Guardar
        </Button>
      </div>
    </form>
  );
}

/**
 * Account selector dropdown.
 */
function AccountSelect({
  value,
  onValueChange,
  accounts,
  placeholder,
}: {
  value: string;
  onValueChange: (value: string) => void;
  accounts: Account[];
  placeholder: string;
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {accounts.map((account) => (
          <SelectItem key={account.id} value={account.id}>
            <span className="font-mono text-xs mr-2">{account.code}</span>
            {account.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
